import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { CampaignStatus } from "@/components/StatusPill";
import { ChannelStatsMini, ChannelStatsModule } from "@/components/ChannelStatsModule";
import {
    Activity, AlertCircle, ArrowUpRight, CheckCircle2, FlaskConical, Mail,
    Megaphone, MessageSquare, SkipForward, Tag, Upload, Users,
} from "lucide-react";

// Honest labels — counts come from the app's own communications collection.
// "Accepted" means the provider's success status (Twilio 201 queued / SendGrid 202 accepted).
// We do NOT use "Delivered" here — that requires real webhooks.
const RANGES = [
    { id: "today", label: "Today" },
    { id: "7d", label: "Last 7 Days" },
    { id: "30d", label: "Last 30 Days" },
];

function CampaignCard({ c }) {
    const stats = c.channel_stats || {};
    const showSms = !!stats.sms;
    const showEmail = !!stats.email;
    const channelLabel = c.channel === "both" ? "SMS + Email" : c.channel === "sms" ? "SMS" : "Email";
    const last = c.last_activity_at ? new Date(c.last_activity_at) : null;

    return (
        <Link
            to={`/campaigns/${c.id}`}
            className="block bg-white border border-border rounded-xl p-5 hover:border-primary/40 hover:shadow-md transition-all"
            data-testid={`running-campaign-${c.id}`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-900 truncate">{c.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                        <span className="uppercase tracking-wider">{channelLabel}</span>
                        {(c.lead_type_names || []).length > 0 && (
                            <>
                                <span aria-hidden>·</span>
                                <span className="truncate">{(c.lead_type_names || []).join(", ")}</span>
                            </>
                        )}
                    </div>
                </div>
                <CampaignStatus status={c.status} />
            </div>
            <div className={`mt-4 grid gap-3 ${showSms && showEmail ? "md:grid-cols-2 grid-cols-1" : "grid-cols-1"}`}>
                {showSms && <ChannelStatsMini channel="sms" stats={stats.sms} testid={`card-${c.id}-sms`} />}
                {showEmail && <ChannelStatsMini channel="email" stats={stats.email} testid={`card-${c.id}-email`} />}
            </div>
            <div className="mt-3 flex items-center justify-between text-[11px] text-muted-foreground">
                <span>{c.send_window?.mode === "global" ? "Global hours" : c.send_window?.mode === "custom" ? "Custom window" : "Anytime"} · {c.send_rate_per_hour}/hr</span>
                <span>{last ? `Last activity: ${last.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "—"}</span>
            </div>
        </Link>
    );
}

function ImportRow({ b }) {
    const date = b.created_at ? new Date(b.created_at) : null;
    return (
        <li className="flex items-start gap-3 py-2.5" data-testid={`recent-import-${b.id}`}>
            <Upload className="h-4 w-4 text-muted-foreground mt-1 shrink-0" />
            <div className="flex-1 min-w-0">
                <div className="text-sm text-gray-900 truncate">{b.filename}</div>
                <div className="text-[11px] text-muted-foreground flex flex-wrap gap-x-3">
                    {b.lead_type_name && (
                        <span className="flex items-center gap-1">
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: b.lead_type_color || "#94a3b8" }} />
                            {b.lead_type_name}
                        </span>
                    )}
                    <span className="text-emerald-700">{(b.imported_count ?? 0).toLocaleString()} imported</span>
                    {b.duplicate_count ? <span className="text-amber-700">{b.duplicate_count.toLocaleString()} dupes</span> : null}
                    {b.invalid_phone_count ? <span className="text-rose-700">{b.invalid_phone_count} bad phones</span> : null}
                    {b.invalid_count ? <span className="text-orange-700">{b.invalid_count} invalid</span> : null}
                </div>
            </div>
            <div className="text-[11px] text-muted-foreground whitespace-nowrap">
                {date ? date.toLocaleDateString([], { month: "short", day: "numeric" }) : ""}
                {date && date.toLocaleDateString() === new Date().toLocaleDateString() && (
                    <> · {date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</>
                )}
            </div>
        </li>
    );
}

// Honest dashboard labels: success statuses all collapse to "Accepted" because we don't
// have delivery webhook receipts yet. Failure/skip/reply states are surfaced as-is.
const COMM_STATUS_LABEL = {
    queued: "Accepted",
    accepted: "Accepted",
    sending: "Accepted",
    sent: "Accepted",
    delivered: "Accepted",
    failed: "Failed",
    skipped: "Skipped",
    replied: "Replied",
    opted_out: "Opted out",
};

function CommRow({ c, channel }) {
    const isFail = c.status === "failed";
    const isSkip = c.status === "skipped";
    const Icon = isFail ? AlertCircle : isSkip ? SkipForward : ArrowUpRight;
    const tone = isFail
        ? "text-rose-600 bg-rose-50"
        : isSkip
            ? "text-amber-700 bg-amber-50"
            : channel === "sms"
                ? "text-emerald-700 bg-emerald-50"
                : "text-blue-700 bg-blue-50";
    return (
        <div className="flex items-start gap-3 py-2.5">
            <div className={`h-7 w-7 rounded-md flex items-center justify-center shrink-0 ${tone}`}>
                <Icon className="h-3.5 w-3.5" strokeWidth={2} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-sm text-gray-900 truncate">
                    {c.lead_name || c.to || (channel === "sms" ? "SMS recipient" : "Email recipient")}
                    {c.campaign_name && (
                        <span className="text-muted-foreground"> · {c.campaign_name}</span>
                    )}
                </div>
                <div className="text-[11px] text-muted-foreground truncate flex gap-2">
                    <span>{COMM_STATUS_LABEL[c.status] || c.status}</span>
                    {c.to && <span className="font-mono truncate">{c.to}</span>}
                </div>
            </div>
            <div className="text-[11px] text-muted-foreground whitespace-nowrap">
                {c.created_at ? new Date(c.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
            </div>
        </div>
    );
}

function ChannelDashboardSection({ channel, stats, recent }) {
    if (!stats) return null;
    const isSms = channel === "sms";
    const accentBorder = isSms ? "border-emerald-200/70" : "border-blue-200/70";
    const accentBg = isSms ? "bg-emerald-50" : "bg-blue-50";
    const accentText = isSms ? "text-emerald-700" : "text-blue-700";
    const HeaderIcon = isSms ? MessageSquare : Mail;
    return (
        <section
            className={`rounded-2xl border ${accentBorder} bg-gradient-to-br from-white to-gray-50/40 p-5 shadow-sm space-y-5`}
            data-testid={`dashboard-${channel}-module`}
        >
            <header className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                    <span className={`h-9 w-9 rounded-lg flex items-center justify-center ${accentBg} ${accentText}`}>
                        <HeaderIcon className="h-4 w-4" strokeWidth={2} />
                    </span>
                    <div>
                        <div className="text-base font-display font-semibold text-gray-900">
                            {isSms ? "SMS Activity" : "Email Activity"}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                            via {isSms ? "Twilio" : "SendGrid"} · app-owned counters
                        </div>
                    </div>
                </div>
                <Link
                    to="/campaigns"
                    className={`text-xs ${accentText} hover:underline`}
                    data-testid={`dashboard-${channel}-view-campaigns`}
                >
                    {stats.campaigns_running} running
                </Link>
            </header>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Tile
                    label={isSms ? "Accepted by Twilio" : "Accepted by SendGrid"}
                    value={stats.accepted}
                    tone={isSms ? "emerald" : "blue"}
                    testid={`dashboard-${channel}-accepted`}
                />
                <Tile
                    label={isSms ? "Failed SMS" : "Failed Email"}
                    value={stats.failed}
                    tone={stats.failed ? "rose" : "gray"}
                    testid={`dashboard-${channel}-failed`}
                />
                <Tile
                    label={isSms ? "Skipped SMS" : "Skipped Email"}
                    value={stats.skipped}
                    tone={stats.skipped ? "amber" : "gray"}
                    testid={`dashboard-${channel}-skipped`}
                />
                <Tile
                    label={`${isSms ? "SMS" : "Email"} Campaigns Running`}
                    value={stats.campaigns_running}
                    sub={stats.campaigns_paused ? `${stats.campaigns_paused} paused` : null}
                    tone={isSms ? "emerald" : "blue"}
                    testid={`dashboard-${channel}-running`}
                />
            </div>

            <div>
                <div className="text-xs font-medium text-gray-700 mb-2 flex items-center justify-between">
                    <span>{isSms ? "SMS Progress" : "Email Progress"}</span>
                    <span className="tabular text-muted-foreground">
                        {stats.processed.toLocaleString()} processed
                    </span>
                </div>
                <ProgressInline
                    accepted={stats.accepted}
                    failed={stats.failed}
                    skipped={stats.skipped}
                    accentBar={isSms ? "bg-emerald-500" : "bg-blue-500"}
                />
            </div>

            <div>
                <div className="flex items-center justify-between mb-1">
                    <h3 className="text-xs font-medium text-gray-700">
                        Recent {isSms ? "SMS" : "Email"} Activity
                    </h3>
                    <Link to="/activity" className="text-[11px] text-muted-foreground hover:text-gray-900">View all</Link>
                </div>
                <div className="divide-y divide-border" data-testid={`dashboard-${channel}-recent`}>
                    {recent.length === 0 ? (
                        <div className="text-xs text-muted-foreground py-3">
                            No recent {isSms ? "SMS" : "Email"} activity in this range.
                        </div>
                    ) : recent.slice(0, 5).map(r => <CommRow key={r.id} c={r} channel={channel} />)}
                </div>
            </div>
        </section>
    );
}

function ProgressInline({ accepted, failed, skipped, accentBar }) {
    const total = (accepted || 0) + (failed || 0) + (skipped || 0);
    if (!total) {
        return <div className="h-2 rounded-full bg-gray-100" />;
    }
    const pAcc = (accepted / total) * 100;
    const pFail = (failed / total) * 100;
    const pSkip = (skipped / total) * 100;
    return (
        <div className="h-2 rounded-full bg-gray-100 overflow-hidden flex">
            <div className={`h-full ${accentBar}`} style={{ width: `${pAcc}%` }} />
            <div className="h-full bg-rose-400" style={{ width: `${pFail}%` }} />
            <div className="h-full bg-amber-400" style={{ width: `${pSkip}%` }} />
        </div>
    );
}

function Tile({ label, value, sub, tone, testid }) {
    const toneCls = {
        emerald: "bg-white border-emerald-100",
        blue: "bg-white border-blue-100",
        rose: "bg-white border-rose-100",
        amber: "bg-white border-amber-100",
        gray: "bg-white border-border",
    }[tone] || "bg-white border-border";
    return (
        <div className={`rounded-lg border ${toneCls} p-3`} data-testid={testid}>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground leading-tight">
                {label}
            </div>
            <div className="font-display text-2xl font-semibold tabular text-gray-900 mt-1">
                {(value ?? 0).toLocaleString()}
            </div>
            {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
        </div>
    );
}

export default function Dashboard() {
    const [range, setRange] = useState("today");
    const [summary, setSummary] = useState(null);
    const [running, setRunning] = useState([]);
    const [smsRecent, setSmsRecent] = useState([]);
    const [emailRecent, setEmailRecent] = useState([]);
    const [imports, setImports] = useState([]);

    const rangeLabel = useMemo(() => RANGES.find(r => r.id === range)?.label || "Today", [range]);

    const load = async () => {
        const [s, r, sms, em, im] = await Promise.all([
            api.get("/dashboard/summary", { params: { range } }),
            api.get("/dashboard/running-campaigns"),
            api.get("/dashboard/recent-channel-activity", { params: { channel: "sms", limit: 5 } }),
            api.get("/dashboard/recent-channel-activity", { params: { channel: "email", limit: 5 } }),
            api.get("/dashboard/recent-imports", { params: { limit: 6 } }),
        ]);
        setSummary(s.data);
        setRunning(r.data);
        setSmsRecent(sms.data);
        setEmailRecent(em.data);
        setImports(im.data);
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 10000);
        return () => clearInterval(t);
        // eslint-disable-next-line
    }, [range]);

    const totalLeads = summary?.total_leads ?? 0;
    const leadsImported = summary?.leads_imported ?? 0;

    return (
        <div className="space-y-8" data-testid="dashboard-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight text-gray-900">
                        Dashboard
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Operations overview · {rangeLabel.toLowerCase()}
                    </p>
                </div>
                <div className="inline-flex border border-border rounded-md overflow-hidden" data-testid="dashboard-range">
                    {RANGES.map(r => (
                        <button
                            key={r.id}
                            onClick={() => setRange(r.id)}
                            data-testid={`range-${r.id}`}
                            className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                                range === r.id
                                    ? "bg-primary/10 text-primary"
                                    : "bg-white text-gray-600 hover:bg-gray-50"
                            }`}
                        >
                            {r.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Cross-channel summary strip — minimal, neutral. The SMS/Email modules below carry the real per-channel detail. */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Tile
                    label="Active Campaigns"
                    value={summary?.active_campaigns ?? 0}
                    sub={`${summary?.paused_campaigns ?? 0} paused · ${summary?.scheduled_campaigns ?? 0} scheduled`}
                    tone="gray"
                    testid="stat-active-campaigns"
                />
                <Tile
                    label="Leads Imported"
                    value={leadsImported}
                    sub={`${totalLeads.toLocaleString()} total in DB`}
                    tone="gray"
                    testid="stat-leads-imported"
                />
                <Tile
                    label="Replies"
                    value={summary?.replies ?? 0}
                    sub="inbound across both channels"
                    tone="gray"
                    testid="stat-replies"
                />
                <Tile
                    label="Failed (All Channels)"
                    value={summary?.failed ?? 0}
                    sub={(summary?.skipped ?? 0) ? `${summary.skipped} skipped` : null}
                    tone={(summary?.failed ?? 0) ? "rose" : "gray"}
                    testid="stat-failed"
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <ChannelDashboardSection
                    channel="sms"
                    stats={summary?.sms}
                    recent={smsRecent}
                />
                <ChannelDashboardSection
                    channel="email"
                    stats={summary?.email}
                    recent={emailRecent}
                />
            </div>

            <section>
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-base font-medium text-gray-900">Active Campaigns</h2>
                    <Link to="/campaigns" className="text-sm text-primary hover:underline" data-testid="view-all-campaigns">
                        View all
                    </Link>
                </div>
                {running.length === 0 ? (
                    <div className="bg-white border border-border rounded-xl p-8 text-center text-sm text-muted-foreground">
                        No campaigns are running. <Link to="/campaigns/new" className="text-primary">Create one →</Link>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {running.map(c => <CampaignCard key={c.id} c={c} />)}
                    </div>
                )}
            </section>

            <section className="bg-white border border-border rounded-xl p-5 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-base font-medium text-gray-900">Recent Imports</h2>
                    <Link to="/imports" className="text-sm text-primary hover:underline">View all</Link>
                </div>
                {imports.length === 0 ? (
                    <div className="text-sm text-muted-foreground">No imports yet.</div>
                ) : (
                    <ul className="divide-y divide-border" data-testid="recent-imports-list">
                        {imports.map(b => <ImportRow key={b.id} b={b} />)}
                    </ul>
                )}
            </section>
        </div>
    );
}
